import React, { useCallback, useContext, useEffect, useRef, useState } from "react";
import axios from "axios";
import { useFormik } from "formik";
import {
  Breadcrumb,
  Button,
  Card,
  Col,
  Container,
  Row,
} from "react-bootstrap";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import {
  faEnvelope,
  faPen,
  faSync,
  faUser,
  faUsers,
  faStore,
} from "@fortawesome/free-solid-svg-icons";
import { confirmAlert } from "react-confirm-alert";
import Loader from "../../components/Loader.js";
import { ShowConfirm } from "../../components/Alerts.js";
import WhatsAppSystemSettingsCard from "../../components/WhatsAppSystemSettingsCard.js";
import EmailTemplateEditorModal from "../../components/EmailTemplateEditorModal.js";
import NotyfContext from "../../contexts/NotyfContext.js";
import useLayout from "../../hooks/useLayout.js";

const TEMPLATE_USER_TYPES = [
  {
    type: "0",
    label: "Participantes",
    icon: faUser,
    defaultName: "Modelo padrão - Participantes",
    description: "Modelo enviado principalmente após o cadastro inicial de participantes gerais.",
  },
  {
    type: "1",
    label: "Usuários gestores",
    icon: faUsers,
    defaultName: "Modelo padrão - Usuários gestores",
    description: "Modelo de credenciamento e acesso para gestores e administradores do evento.",
  },
  {
    type: "2",
    label: "Expositores",
    icon: faStore,
    defaultName: "Modelo padrão - Expositores",
    description: "Modelo para estandes, expositores e patrocinadores credenciados no evento.",
  },
];

const INITIAL_TEMPLATES = {
  "0": {
    name: "Modelo padrão - Participantes",
    subject: "Bem-vindo(a) ao evento!",
    from_name: "Equipe do Evento",
    reply_to: "aplicacao@r1eventech.com.br",
    html: "",
  },
  "1": {
    name: "Modelo padrão - Usuários gestores",
    subject: "Bem-vindo(a) ao evento!",
    from_name: "Equipe do Evento",
    reply_to: "aplicacao@r1eventech.com.br",
    html: "",
  },
  "2": {
    name: "Modelo padrão - Expositores",
    subject: "Bem-vindo(a) ao evento!",
    from_name: "Equipe do Evento",
    reply_to: "aplicacao@r1eventech.com.br",
    html: "",
  },
};

const SystemSettings = () => {
  const notyf = useContext(NotyfContext);
  const { setLoading } = useLayout();
  const [initialLoading, setInitialLoading] = useState(true);
  const [refreshing, setRefreshing] = useState(false);
  const [saving, setSaving] = useState(false);
  const [showModal, setShowModal] = useState(false);
  const [registeredLoaderKeys, setRegisteredLoaderKeys] = useState([]);
  const settingsLoadersRef = useRef({});
  const initialLoadStartedRef = useRef(false);
  const whatsappHasSenderRef = useRef(false);

  const formik = useFormik({
    initialValues: {
      templates: INITIAL_TEMPLATES,
    },
    onSubmit: () => {},
  });

  const saveSystemTemplates = async ({ templates }) => {
    setSaving(true);
    try {
      const response = await axios.put("/api/system/settings/email-templates", { templates });
      if (response.data?.templates) {
        formik.setValues({
          templates: {
            ...INITIAL_TEMPLATES,
            ...response.data.templates,
          },
        });
      }
      notyf?.open({
        type: "success",
        message: "Todos os modelos de e-mail foram salvos com sucesso!",
      });
      setShowModal(false);
      return response;
    } catch (error) {
      notyf?.open({
        type: "danger",
        message: error.response?.data?.message || "Erro ao salvar modelos de e-mail.",
      });
      return null;
    } finally {
      setSaving(false);
    }
  };

  const loadTemplates = useCallback(async () => {
    try {
      const response = await axios.get("/api/system/settings/email-templates");
      if (response.data?.templates) {
        formik.setValues({
          templates: {
            ...INITIAL_TEMPLATES,
            ...response.data.templates,
          },
        });
      }
    } catch (error) {
      if (notyf) {
        notyf.open({
          type: "warning",
          message: "Não foi possível carregar os modelos do servidor. Usando padrões locais.",
        });
      }
    }
  }, [notyf]); // eslint-disable-line react-hooks/exhaustive-deps

  const registerSettingsLoader = useCallback((key, loader) => {
    settingsLoadersRef.current[key] = loader;
    setRegisteredLoaderKeys((current) => (
      current.includes(key) ? current : [...current, key]
    ));
    return () => {
      if (settingsLoadersRef.current[key] === loader) {
        delete settingsLoadersRef.current[key];
      }
    };
  }, []);

  const loadAllSettings = useCallback(async ({ initial = false, showGlobalLoading = false, synchronizeCatalog = false, synchronizeSender = false } = {}) => {
    if (initial) setInitialLoading(true);
    if (showGlobalLoading) {
      setRefreshing(true);
      setLoading(true);
    }

    const loaders = Object.entries(settingsLoadersRef.current);
    try {
      const templateLoader = loaders.find(([key]) => key === "whatsappTemplates")?.[1];
      const settingsLoaders = loaders.filter(([key]) => key !== "whatsappTemplates");
      const settingsResults = await Promise.allSettled([
        loadTemplates(),
        // The R1 sender is synchronized with Meta only after a confirmed manual refresh.
        ...settingsLoaders.map(async ([key, loader]) => ({ key, result: await loader(key === "whatsapp" ? { synchronize: synchronizeSender } : undefined) })),
      ]);
      const whatsappResult = settingsResults.slice(1).find((result) => (
        result.status === "fulfilled" && result.value.key === "whatsapp"
      ));
      if (whatsappResult) whatsappHasSenderRef.current = whatsappResult.value.result?.hasSender === true;
      if (templateLoader) {
        await templateLoader({
          synchronize: synchronizeCatalog && whatsappResult?.value.result?.ready === true,
        });
      }
    } finally {
      if (initial) setInitialLoading(false);
      if (showGlobalLoading) {
        setRefreshing(false);
        setLoading(false);
      }
    }
  }, [loadTemplates, setLoading]);

  useEffect(() => {
    const requiredLoaders = ["whatsapp", "whatsappTemplates"];
    if (
      initialLoadStartedRef.current
      || !requiredLoaders.every((key) => registeredLoaderKeys.includes(key))
    ) return;

    initialLoadStartedRef.current = true;
    loadAllSettings({ initial: true });
  }, [loadAllSettings, registeredLoaderKeys]);

  // Programmatic refresh after saves: reads settings and the template catalog
  // without touching the sender's Meta status.
  const handleSettingsRefresh = ({ synchronizeSender = false } = {}) => loadAllSettings({
    showGlobalLoading: true,
    synchronizeCatalog: true,
    synchronizeSender,
  });

  // Manual refresh: a Meta sync can suspend the R1 sender and pause its
  // campaigns, so the operator confirms it first.
  const confirmSettingsRefresh = () => {
    if (!whatsappHasSenderRef.current) {
      handleSettingsRefresh();
      return;
    }
    confirmAlert({
      closeOnEscape: false,
      closeOnClickOutside: false,
      customUI: ({ onClose }) => (
        <ShowConfirm
          title="Sincronizar o número R1 com a Meta?"
          description="Atualizar também consulta a Meta sobre o número R1. Se a Meta recusar a credencial, não responder ao renovar a inscrição do webhook, indicar qualidade baixa ou informar que o número não está conectado, o número será suspenso e as campanhas agendadas ou em andamento serão pausadas. Elas não serão retomadas automaticamente."
          textConfirm="Atualizar e sincronizar"
          action={() => handleSettingsRefresh({ synchronizeSender: true })}
          onClose={onClose}
        />
      ),
    });
  };

  return (
    <>
      {initialLoading && <Loader />}
      <div className={initialLoading ? "d-none" : undefined}>
        <Button
          type="button"
          variant="primary"
          id="btn-update"
          className="btn update-side-panel-button"
          onClick={confirmSettingsRefresh}
          disabled={refreshing || saving}
          title="Atualizar configurações do banco"
          aria-label="Atualizar configurações do banco"
        >
          <FontAwesomeIcon icon={faSync} spin={refreshing} size="lg" />
        </Button>

        <Container className="p-0 d-flex flex-column container w-100">
        {/* Header & Breadcrumb */}
        <Row className="mx-0">
          <Col xs="auto" className="order-1 px-0 me-3">
            <Breadcrumb>
              <Breadcrumb.Item active>Configurações do sistema</Breadcrumb.Item>
            </Breadcrumb>
            <h6 className="text-muted mb-3">
              Espaço para definir configurações que impactam todos os eventos
            </h6>
          </Col>
        </Row>

        {/* Section Header */}
        <div className="textHeaderAplicativo mb-3 col">
          <h5 className="card-title mb-0">Quadro de Configurações</h5>
        </div>

        {/* Trello-style Board Layout */}
        <div
          className="d-flex flex-row overflow-auto pb-4 gap-3 align-items-start"
          style={{ minHeight: "480px" }}
        >
          {/* Column: Configurações de E-mail */}
          <div
            className="rounded p-2"
            style={{
              flex: "1 0 320px",
              backgroundColor: "#f4f5f7",
              border: "1px solid #e2e4e9",
            }}
          >
            {/* Column Header */}
            <div className="d-flex justify-content-between align-items-center px-2 py-2 mb-2">
              <div className="d-flex align-items-center gap-2">
                <FontAwesomeIcon icon={faEnvelope} className="text-primary" />
                <h6 className="mb-0 fw-bold text-dark">
                  Configurações de E-mail
                </h6>
              </div>
            </div>

            {/* Single Card inside Column */}
            <Card
              className="border shadow-sm mb-0"
              style={{ cursor: "pointer", transition: "transform 0.15s ease, box-shadow 0.15s ease" }}
              onClick={() => setShowModal(true)}
            >
              <Card.Body className="p-3">
                <div className="mb-2">
                  <h6 className="fw-bold text-dark mb-0">
                    Modelos padrão de E-mail
                  </h6>
                </div>

                <p className="text-muted small mb-3">
                  Configure os 3 modelos de e-mail usados no "Envio de email automático após registro"
                </p>

                <div className="border-top pt-2 mt-2">
                  <div className="d-flex flex-column gap-1 small">
                    {TEMPLATE_USER_TYPES.map((cfg) => {
                      const tpl = formik.values.templates[cfg.type] || {};
                      return (
                        <div key={cfg.type} className="d-flex justify-content-between align-items-center text-muted">
                          <span className="d-flex align-items-center gap-1 flex-shrink-0">
                            <FontAwesomeIcon icon={cfg.icon} size="xs" />
                            <span>{cfg.label}:</span>
                          </span>
                          <span className="fw-semibold text-dark text-truncate ms-2" style={{ maxWidth: "220px" }}>
                            {tpl.name || cfg.defaultName}
                          </span>
                        </div>
                      );
                    })}
                  </div>
                </div>

                <div className="d-flex justify-content-end align-items-center mt-3 pt-2 border-top">
                  <Button
                    variant="primary"
                    size="sm"
                    className="py-1 px-3"
                    onClick={(e) => {
                      e.stopPropagation();
                      setShowModal(true);
                    }}
                  >
                    <FontAwesomeIcon icon={faPen} className="me-1" />
                    Editar
                  </Button>
                </div>
              </Card.Body>
            </Card>
          </div>
          <WhatsAppSystemSettingsCard
            onRegisterLoader={registerSettingsLoader}
            onRefresh={() => handleSettingsRefresh()}
          />
        </div>

        <EmailTemplateEditorModal
          show={showModal}
          onHide={() => setShowModal(false)}
          mode="system"
          templates={formik.values.templates}
          userTypes={TEMPLATE_USER_TYPES}
          saving={saving}
          onSave={saveSystemTemplates}
        />
        </Container>
      </div>
    </>
  );
};

export default SystemSettings;
